import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  of,
  throwError,
} from 'rxjs';
import moment from 'moment-timezone';
import { MatDateFormats } from '@angular/material/core';
import { DomSanitizer } from '@angular/platform-browser';
import * as LZString from 'lz-string';
import { ApiService } from '../api-service/api.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import * as CryptoJS from 'crypto-js';
import { environment } from 'src/environment/environment';
import { Router } from '@angular/router';

interface SelectedFiles {
  img: File[];
  file: File[];
}

export const CUSTOM_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Injectable({
  providedIn: 'root',
})
export class SharedService {

  private portalId: string = environment.portalId;
  readonly DATE_FORMAT: string = 'D/MM/YYYY';
  readonly API_CHART_DATE_FORMAT: string = 'YYYY-MM-DD';
  readonly ENCRYPTION_KEY = '4bX9z3vQN2WcE7YpJgHdLrMfTuXsaK1!';
  readonly IV_LENGTH = 16;
  private dataArray: any[] = [];
  public userCredentials: any = null;
  readonly PORTAL_CONFIG: string = 'portalConfig';
  private userStore: { [key: string]: any } = {};
  readonly ISSUE_DATA: string = 'issueRecords';
  readonly TREE_NODE_DATA: string = 'treeData';

  private formData: { [key: string]: any } = { img: [], file: [] };

  previewFileCount: { [key: string]: number } = {};
  loadedFilesList: { [key: string]: any[] } = {};

  private calibrationDataSubject = new BehaviorSubject<any[] | null>(null);
  calibrationData$ = this.calibrationDataSubject.asObservable();
  private storageKey = 'calibrationRecord';
  private calib_verifKey = 'calibrationVerification';

  public selectedFiles: { [key: string]: SelectedFiles } = {
    default: { img: [], file: [] },
  };

  supportedImageExts = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];
  unsupportedImageExts = ['.dng', '.raw', '.cr2', '.nef', '.arw', '.heic'];
  supportedFileExts = ['.pdf', '.docx', '.xlsx', '.txt'];

  dateRange: { start: string; end: string } = {
    start: moment().startOf('month').format(this.DATE_FORMAT),
    end: moment().endOf('month').format(this.DATE_FORMAT),
  };
  logdateRange: { start: string; end: string } = {
    start: moment().startOf('day').format(this.DATE_FORMAT),
    end: moment().endOf('day').format(this.DATE_FORMAT),
  };

  private issuesDataSubject = new BehaviorSubject<any[]>(
    this.getIssuesDataFromSession(),
  );
  private issueRecordsSubject = new BehaviorSubject<any[]>(
    this.getIssuesRecordsFromSession(),
  );
  private CalibrationAndVerification = new BehaviorSubject<any[]>(
    this.getCalibrationAndVerification(),
  );

  private userPermissions = new BehaviorSubject<any>([]);
  userPermissions$ = this.userPermissions.asObservable();

  private readonly STORAGE_KEYS: string[] = [
    'username',
    'email',
    'permissionlevel',
    'timeZone',
    'roleRank',
    'roleId',
    'portalName',
    'portalDisplayName',
    'userId',
  ];

  constructor(
    private sanitizer: DomSanitizer,
    private service: ApiService,
    private ngxLoader: NgxUiLoaderService,
    private router: Router,
  ) {
    this.loadCalibrationDataFromStorage();
    this.STORAGE_KEYS.forEach((key) => {
      const value = sessionStorage.getItem(key);
      if (!value) return;
      if (value) {
        this.userStore[key] = value;
      }
    });
  }

  getPortalConfiguration() {
    if (!this.userCredentials) {
      const storedCredentials = sessionStorage.getItem(this.PORTAL_CONFIG);
      if (storedCredentials)
        this.userCredentials = JSON.parse(storedCredentials);
    }
    return this.userCredentials;
  }

  getUserPermissions(): any[] {
    const creds = this.getPortalConfiguration();
    const permissions = creds?.permissions ?? [];
    return permissions;
  }

  hasPermission(slug: string): boolean {
    const permissions = this.getUserPermissions() || [];

    return permissions.some((module: any) => {
      if (module?.moduleSlug === slug) return true;
      return module?.subModules?.some((sub: any) => sub?.moduleSlug === slug) ?? false;
    });
  }

  hasActionPermission(moduleSlug: string, action: string): boolean {
    const permissions = this.getUserPermissions();
    const findMod = (mods: any[]): any | null => {
      for (const m of mods) {
        if (m.moduleSlug === moduleSlug) return m;
        if (m.subModules?.length) {
          const hit = findMod(m.subModules);
          if (hit) return hit;
        }
      }
      return null;
    };
    const mod = findMod(permissions);
    if (!mod || !mod.enabled) return false;
    return (mod.allowedActions as string[] | undefined)?.includes(action) ?? false;
  }

  public setUserProperty(key: string, value: any): void {
    this.userStore[key] = value;
    sessionStorage.setItem(key, value);
  }

  setPortalConfiguration(credentials: any) {
    this.userCredentials = credentials;
    sessionStorage.setItem(this.PORTAL_CONFIG, JSON.stringify(credentials));
  }

  setModuleList(module: any[]): void {
    this.userPermissions.next(module);
  }

  get(key: string): any {
    if (this.userStore[key] !== undefined) {
      return this.userStore[key];
    }

    const stored = sessionStorage.getItem(key);
    if (stored !== null) {
      this.userStore[key] = stored;
      return stored;
    }

    return null;
  }

  setIssueRecords(data: any[]): void {
    this.issueRecordsSubject.next(data);
    this.dataArray = data;
    sessionStorage.setItem('issueRecords', JSON.stringify(data));
  }

  findIssueRecordById(issueRef: string): any | null {
    if (!this.dataArray || this.dataArray.length === 0) {
      const storedData = sessionStorage.getItem('issueRecords');
      if (storedData) {
        this.dataArray = JSON.parse(storedData);
      } else {
        return null;
      }
    }

    const foundRecord = this.dataArray.find((item) => item._id === issueRef);
    return foundRecord || null;
  }

  getTreeNode(id: string): any | null {
    const cached = sessionStorage.getItem(this.TREE_NODE_DATA);

    if (!cached) {
      return null;
    }

    try {
      const treeNodes = JSON.parse(cached) as any[];
      return (
        treeNodes.find((node) => node?._id === id || node?.id === id) || null
      );
    } catch (error) {
      console.error(
        'Failed to parse tree node data from sessionStorage',
        error,
      );
      return null;
    }
  }

  setIssuesData(data: any[]): void {
    this.issuesDataSubject.next(data);
    sessionStorage.setItem('treeData', JSON.stringify(data));
  }

  private getIssuesDataFromSession(): any[] {
    const data = sessionStorage.getItem('treeData');
    return data ? JSON.parse(data) : [];
  }

  private getIssuesRecordsFromSession(): any[] {
    const data = sessionStorage.getItem('treeData');
    return data ? JSON.parse(data) : [];
  }

  getIssuesData(): any[] {
    return this.issuesDataSubject.value;
  }

  loadCalibrationDataFromStorage(): void {
    const stored = sessionStorage.getItem(this.storageKey);
    if (stored) {
      try {
        const decompressed = LZString.decompress(stored);
        const parsed = JSON.parse(decompressed || '[]');
        this.calibrationDataSubject.next(parsed);
      } catch (e) {
        console.error('Failed to load or parse calibration data:', e);
        this.calibrationDataSubject.next(null); // fallback to null
      }
    } else {
      this.calibrationDataSubject.next(null);
    }
  }

  fetchIssueRecordsByPortalRef(portalRef: any) {
    this.ngxLoader.start();
    let reqBody: any = { portalId: portalRef };

    return new Promise((resolve, reject) => {
      this.service.postRequest('list-issue', reqBody).subscribe((item: any) => {
        this.ngxLoader.stop();
        let tableData = item.data;
        sessionStorage.setItem('issueRecords', JSON.stringify(tableData));
        this.setIssueRecords(tableData);
        resolve(tableData);
      });
    });
  }

  saveCalibrationData(data: any[]): void {
    try {
      const compressed = LZString.compress(JSON.stringify(data));
      sessionStorage.setItem(this.storageKey, compressed);
      this.calibrationDataSubject.next(data);
    } catch (e) {
      console.error('Error saving calibration data:', e);
    }
  }

  SetCalibrationAndVerificationData(data: any[]): void {
    this.CalibrationAndVerification.next(data);
    sessionStorage.setItem(this.calib_verifKey, JSON.stringify(data));
  }

  private getCalibrationAndVerification(): any[] {
    const data = sessionStorage.getItem(this.calib_verifKey);
    return data ? JSON.parse(data) : [];
  }

  getCalibrationAndVerificationData(): any[] {
    const data = sessionStorage.getItem(this.calib_verifKey);
    return data ? JSON.parse(data) : [];
  }

  clearCalibrationData(): void {
    sessionStorage.removeItem(this.storageKey);
    this.calibrationDataSubject.next(null);
  }

  convertDateFormat(dateString: string): string {
    if (!dateString) return '';

    const inputDateTime = moment(dateString).tz(this.get('timeZone'));
    if (!inputDateTime.isValid()) {
      console.warn('Invalid date format:', dateString);
      return 'Invalid Date';
    }
    const formattedDateTime = inputDateTime.format('h:mm a, ddd., MMM Do YYYY');
    return formattedDateTime;
  }

  convertDateTime(dateString: string): string {
    if (!dateString) return '';

    const inputDateTime = moment(dateString).tz(this.get('timeZone'));
    if (!inputDateTime.isValid()) {
      console.warn('Invalid date format:', dateString);
      return 'Invalid Date';
    }
    const formattedDateTime = inputDateTime.format();
    return formattedDateTime;
  }

  formatDate(date: any) {
    if (!date) return '';
    const formattedDate = moment(date, 'DD/MM/YYYY HH:mm');
    if (!formattedDate.isValid()) {
      console.error('Invalid date format:', date);
      return '';
    }
    return formattedDate.format('YYYY-MM-DDTHH:mm:ss');
  }

  convertUTCtoTimeZone(ts: any): any {
    const formattedTimestamp = moment
      .utc(ts)
      .tz(this.get('timeZone'))
      .format('D/MM/YYYY HH:mm');
    return formattedTimestamp ? formattedTimestamp : ts;
  }

  convertVisitUTCtoTimeZone(ts: any): any {
    const formattedTimestamp = moment
      .utc(ts)
      .tz(this.get('timeZone'))
      .format('h:mm a, ddd., MMM Do YYYY');

    return formattedTimestamp ? formattedTimestamp : ts;
  }

  convertLocalToPortalTimezone(ts: any) {
    const timeZone1 = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let date = moment(ts, 'DD/MM/YYYY HH:mm')
      .tz(timeZone1, true)
      .tz(this.get('timeZone'), true)
      .utc()
      .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    return date ? date : ts;
  }

  getPortalMaxDate(): Date {
    let now = moment();
    const brisbaneDate = moment().tz(this.get('timeZone')).toDate();
    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    try {
      let brisbaneMoment = moment.tz(brisbaneDate, this.get('timeZone'));
      let localDate = moment.tz(
        brisbaneMoment.format('YYYY-MM-DDTHH:mm:ss'),
        localTimeZone,
      );
      let finalDate = localDate.toDate();
      return finalDate;
    } catch (error) {
      console.error('Timezone conversion error:', error);
      return now.toDate();
    }
  }

  convertzoneToUTC(input: string): string {
    try {
      const utcTime = moment
        .tz(input, 'DD/MM/YYYY HH:mm', this.get('timeZone'))
        .utc()
        .format();
      return utcTime;
    } catch (error) {
      console.error('Error parsing date:', error);
      return 'Invalid date';
    }
  }

  onButtonPress() {
    const datetimeWithZone = new Date().toLocaleString('en-GB', {
      timeZone: this.get('timeZone'),
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true,
    });
    return datetimeWithZone;
  }

  setFormData(data: any, type: string): void {
    this.formData[type] = this.mergeData(
      this.formData[type] || { img: [], file: [] },
      data,
    );
  }

  private mergeData(existingData: any, newData: any): any {
    if (!existingData) {
      return newData;
    } else {
      existingData.img = this.removeDuplicates(existingData.img, newData.img);
      existingData.file = this.removeDuplicates(
        existingData.file,
        newData.file,
      );
      return { ...existingData, ...newData };
    }
  }

  private removeDuplicates(existingFiles: File[], newFiles: File[]): File[] {
    const existingNames = new Set(
      existingFiles.map((file: any) =>
        (file.sourceName || file.name || '').trim().toLowerCase(),
      ),
    );
    const uniqueFiles = [...existingFiles];
    newFiles.forEach((file) => {
      const normalizedFileName = (file.name || '').trim().toLowerCase();
      if (normalizedFileName && !existingNames.has(normalizedFileName)) {
        uniqueFiles.push(file);
        existingNames.add(normalizedFileName);
      }
    });
    return uniqueFiles;
  }

  getFormData(type: string): any {
    const removeDuplicates = (array: any[]) => Array.from(new Set(array));

    return {
      img: removeDuplicates(this.formData[type]?.img || []),
      file: removeDuplicates(this.formData[type]?.file || []),
    };
  }

  clearFormData(type: string): void {
    this.formData[type] = { img: [], file: [] };
  }

  uploadFiles(selectedFiles: { img: File[]; file: File[] }, type: string) {
    let existingFiles: any = this.getFormData(type);
    let processedImages: File[] = [];
    let processedFiles: File[] = [];
    let formDataToSend: { img: File[]; file: File[] } = { img: [], file: [] };

    if (selectedFiles.file && selectedFiles.file.length > 0) {
      processedFiles = processedFiles.concat(Array.from(selectedFiles.file));
    }
    if (selectedFiles.img && selectedFiles.img.length > 0) {
      processedImages = processedImages.concat(Array.from(selectedFiles.img));
    }
    if (existingFiles && existingFiles.img && existingFiles.img.length > 0) {
      formDataToSend.img = [...existingFiles.img, ...processedImages];
    } else {
      formDataToSend.img = processedImages;
    }
    if (existingFiles && existingFiles.file && existingFiles.file.length > 0) {
      formDataToSend.file = [...existingFiles.file, ...processedFiles];
    } else {
      formDataToSend.file = processedFiles;
    }
    this.setFormData(formDataToSend, type);
  }

  getMappedType(captureName: any): string {
    switch (captureName) {
      case 'issue':
        return 'issue';
      case 'newIssue':
        return 'newIssue';
      case 'robot_Verification':
        return 'robot';
      case 'test_Verification':
        return 'testInstrument';
      case 'robot':
        return 'robot_Verification';
      case 'testInstrument':
        return 'test_Verification';
      case 'calibration':
        return 'calibration';
      default:
        return 'maintenance';
    }
  }

  previewFilesAppend(files: any): { img: any[]; files: any[] } {
    if (files.length === 0) return { img: [], files: [] };

    const selectedFiles = files
      .map((item: any) => {
        const typeOfFile = item.type.split('/')[1]?.toLowerCase();
        if (typeOfFile !== 'pdf') return null;

        const fileName = item.filename.toLowerCase();
        const ext = fileName.substring(fileName.lastIndexOf('.'));
        const isSupportedFile = this.supportedFileExts.includes(ext);

        return {
          source: this.sanitizer.bypassSecurityTrustResourceUrl(
            `data:application/pdf;base64,${item.fileUrl}`,
          ),
          sourceName: item.filename,
          type: item.type,
          isSupported: isSupportedFile,
        };
      })
      .filter(Boolean);

    const selectedImg = files
      .map((item: any) => {
        if (!item.type.startsWith('image/')) return null;

        const fileName = item.filename.toLowerCase();
        const ext = fileName.substring(fileName.lastIndexOf('.'));

        const isSupported = this.supportedImageExts.includes(ext);

        return {
          source: `data:${item.type};base64,${item.fileUrl}`,
          sourceName: item.filename,
          type: item.type.split('/')[1]?.toLowerCase(),
          isSupported: isSupported,
        };
      })
      .filter(Boolean);

    return { img: selectedImg, files: selectedFiles };
  }

  convertToFile(file: any): File | null {
    if (file instanceof File) return file;
    const mimeTypeMap: { [key: string]: string } = {
      img: 'image/png',
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      pdf: 'application/pdf',
    };
    if (file && file.source && file.name) {
      const mimeType = mimeTypeMap[file.type] || 'application/octet-stream';
      const base64Data = file.source.split(',')[1];
      const binaryData = atob(base64Data);
      const byteNumbers = new Array(binaryData.length);
      for (let i = 0; i < binaryData.length; i++) {
        byteNumbers[i] = binaryData.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new File([byteArray], file.name, { type: mimeType });
    }
    return null;
  }

  appendUploadedFilesToFormData(payload: {
    formData: FormData;
    filesData: any;
    existingFiles?: any[] | null;
    formDataKey: string;
    convertToFileFn: (file: any) => File | null;
  }): void {
    const { formData, filesData, existingFiles, formDataKey, convertToFileFn } =
      payload;

    const alreadyUploadedNames = existingFiles?.length
      ? existingFiles.map((f: any) => f.filename)
      : [];

    const appendFiles = (fileList: any[], key: string) => {
      fileList.forEach((file: any) => {
        const fileToAppend = convertToFileFn(file);
        if (fileToAppend) {
          formData.append(key, fileToAppend);
        }
      });
    };

    if (filesData?.img?.length) {
      const selectedImages = alreadyUploadedNames.length
        ? filesData.img.filter(
            (f: any) => !alreadyUploadedNames.includes(f.sourceName),
          )
        : filesData.img;
      appendFiles(selectedImages, formDataKey);
    }

    if (filesData?.file?.length) {
      const selectedDocs = alreadyUploadedNames.length
        ? filesData.file.filter(
            (f: any) => !alreadyUploadedNames.includes(f.sourceName),
          )
        : filesData.file;
      appendFiles(selectedDocs, formDataKey);
    }
  }

  encryption(text: string): string {
    const iv = CryptoJS.lib.WordArray.random(this.IV_LENGTH);
    const encrypted = CryptoJS.AES.encrypt(
      text,
      CryptoJS.enc.Utf8.parse(this.ENCRYPTION_KEY),
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      },
    );

    return (
      iv.toString(CryptoJS.enc.Hex) +
      ':' +
      encrypted.ciphertext.toString(CryptoJS.enc.Hex)
    );
  }

  loadSingleIssueData(logIssuRef: string): Promise<any> {
    const payload = { portalId: this.portalId, issueId: logIssuRef };
    return new Promise((resolve, reject) => {
      this.service.postRequest('single-issue', payload).subscribe({
        next: (data) => {
          resolve(data.data);
        },
        error: (err) => {
          reject(err);
        },
      });
    });
  }

  toTimeZone(
    utcDateString: string,
    timeZone: string = 'Australia/Brisbane',
  ): Date {
    if (!utcDateString) return new Date();

    const utcDate = new Date(utcDateString);
    const options: Intl.DateTimeFormatOptions = {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    };

    const localStr = utcDate.toLocaleString('en-AU', options);
    const [datePart, timePart] = localStr.split(', ');
    const [day, month, year] = datePart.split('/').map(Number);
    const [hour, minute, second] = timePart.split(':').map(Number);
    return new Date(year, month - 1, day, hour, minute, second);
  }

  toTimeZoneDisplay(
    date: string | Date,
    timeZone: string = 'Australia/Brisbane',
  ): string {
    const utcDate = new Date(date);
    return utcDate.toLocaleString('en-AU', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }

  fetchSensorList(treeNode: string): Observable<any[]> {
    const payload = {
      aPortalRef: this.portalId,
      aTreeNodeRef: treeNode,
    };

    return this.service.postRequest('treeNode-sensors', payload).pipe(
      map((response: any) => {
        if (response.status) {
          const data = response.data;

          return data.map((sensorData: any) => ({
            id: sensorData._id,
            sensorId: sensorData.sensor._id,
            name: sensorData.sensor.name,
            unit: sensorData.sensor.unit,
            decimalPlaces: sensorData.decimalPlaces,
          }));
        } else {
          return [];
        }
      }),
      catchError((error) => {
        console.error('Error in getSensor request:', error);
        return of([]);
      }),
    );
  }

  submitCalibrationOrVerification(
    action: string,
    formData: FormData,
    additionalData: any,
  ): Observable<any> {
    const endpointIds = {
      created: 'add-calibration-report',
      updated: 'update-calibration-report',
    };

    const endPoint =
      action === 'Created' ? endpointIds.created : endpointIds.updated;
    const { id, type, portalId, sensorId } = additionalData;

    if (action !== 'Created' && id) {
      formData.append('id', id);
    }

    formData.append('portalId', portalId);
    formData.append('sensor', sensorId);
    formData.append('issueRef', additionalData.issueRef);
    formData.append('sensorName', additionalData.sensorName);
    formData.append('treeNode', additionalData.treeNode);
    formData.append('type', type);
    formData.append('status', additionalData.status || '');
    formData.append('robotValue', additionalData.robotValue ?? '');
    formData.append(
      'testInstrumentValue',
      additionalData.testInstrumentValue ?? '',
    );
    formData.append('comparisonResult', additionalData.comparisonResult ?? '');
    formData.append('testInstrumentUnit', additionalData.unit ?? '');
    formData.append('robotTS', additionalData.robotTS ?? '');
    formData.append('testInstrumentTS', additionalData.testInstrumentTS ?? '');
    formData.append('calibrationTS', additionalData.calibrationTS ?? '');
    formData.append('TPSValue', additionalData.TPSValue ?? '');
    formData.append('TPSUnit', additionalData.unit ?? '');

    return this.service.postRequest(endPoint, formData);
  }

  fetchSiteTreeData(portalId: string): Observable<any[]> {
    const payload = { portalId };

    return this.service.postRequest('maintenance/treenode', payload).pipe(
      map((response) => {
        if (response?.status === true && Array.isArray(response.treeNodeData)) {
          return response.treeNodeData;
        } else {
          throw new Error('Tree node data not found or invalid');
        }
      }),
      catchError((err) => {
        const message =
          err?.error?.status === false
            ? 'Something went wrong while fetching tree node data'
            : 'Unexpected error occurred';
        console.error('Error fetching tree node data:', err);
        return throwError(() => new Error(message));
      }),
    );
  }

  /**
   * Single source of truth for the site/tree-node list: reads the
   * sessionStorage cache if present, otherwise fetches and populates it.
   * Replaces what used to be two separate, drifting copies of this
   * cache-check-then-fetch logic in SitelistComponent and HomeComponent.
   */
  getSiteTreeData(portalId: string): Observable<any[]> {
    const cached = sessionStorage.getItem(this.TREE_NODE_DATA);
    if (cached) {
      try {
        return of(JSON.parse(cached));
      } catch (error) {
        console.error('Failed to parse cached tree node data, refetching:', error);
      }
    }

    return this.fetchSiteTreeData(portalId).pipe(
      map((data) => {
        sessionStorage.setItem(this.TREE_NODE_DATA, JSON.stringify(data));
        return data;
      }),
    );
  }

  fetchIssueList(portalId: string): Observable<any[]> {
    const reqBody = { portalId };

    return this.service.postRequest('list-issue', reqBody).pipe(
      map((res: any) => res?.data || []),
      catchError((error) => {
        console.error('Error fetching issue list:', error);
        return of([]); // Return an empty array to keep observable stream alive
      }),
    );
  }

  getSafeSortConfig(): any {
    const config = this.getPortalConfiguration()?.sortMode    
    return {
      sortType: config?.sortType ?? 'manual',
      sortOrder: config?.sortOrder ?? 'asc'
    };
  }

  sortList(list: any[]): any[] {
    const config = this.getSafeSortConfig();

    return list.sort((a: any, b: any) => {

      if (config.sortType === 'alphabetical') {
        const valA = (a.textLabel ?? '').toLowerCase();
        const valB = (b.textLabel ?? '').toLowerCase();
        return config.sortOrder === 'desc' ? valB.localeCompare(valA) : valA.localeCompare(valB);
      }
      const rA = a.rank ?? 0;
      const rB = b.rank ?? 0;

      return config.sortOrder === 'desc' ? rB - rA : rA - rB;
    });
  }

  sortReasonList(list: any[]): any[] {
    const rawConfig = this.getPortalConfiguration()?.sortMode;

    const sortType = rawConfig?.sortType ?? 'manual';
    const sortOrder = rawConfig?.sortOrder ?? 'asc';

    return list.sort((a: any, b: any) => {

      if (sortType === 'alphabetical') {
        const valA = (a.textLabel ?? '').toLowerCase();
        const valB = (b.textLabel ?? '').toLowerCase();

        return sortOrder === 'desc' ? valB.localeCompare(valA) : valA.localeCompare(valB);
      }

      const tA = this.getValidTimestamp(a.ts);
      const tB = this.getValidTimestamp(b.ts);

      return sortOrder === 'desc' ? tB - tA : tA - tB;
    });
  }

  private getValidTimestamp(ts: any): number {
    if (!ts || typeof ts !== 'string') return 0;

    // invalid date case
    if (ts.includes('No data')) return 0;

    const time = new Date(ts).getTime();
    return isNaN(time) ? 0 : time;
  }

  filterPermissionSensors(treeNode: string, moduleId: string, sensors: any[]): any[] {
    if (!sensors || sensors.length === 0) {
      return [];
    }

    const modules: any[] = this.getUserPermissions();
    const permissions: any[] = this.getModulePermissions(moduleId, modules);

    if (!permissions || permissions.length === 0) {
      return [];
    }

    const devicePermission = permissions.find((p: any) => p.treeNode === treeNode);

    if (!devicePermission) {
      return [];
    }

    const allowedSensorIds = new Set((devicePermission.sensor || []).map((s: any) => typeof s === 'object' ? (s?.id ?? s?._id) : s));

    return sensors.filter((sensor: any) => {
      const sensorId = sensor?.productSensor ?? sensor?._id ?? sensor?.id;
      return !!sensorId && allowedSensorIds.has(sensorId);
    });
  }

  filterPermittedDevices(deviceList: any[]): any[] {

    if (!deviceList || deviceList.length === 0) {
      return [];
    }

    const { moduleId } = this.getCurrentModule();
    const modules: any[] = this.getUserPermissions();
    const permissions: any[] = this.getModulePermissions(moduleId, modules);

    if (!permissions || permissions.length === 0) {
      return [];
    }

    const allowedDevices = new Set(permissions.map((p: any) => p.treeNode));

    const getDeviceId = (item: any): string | undefined =>
      item?.treeNodeId ?? item?._id ?? item?.id;

    return deviceList.filter((item: any) => {
      const deviceId = getDeviceId(item);
      return !!deviceId && allowedDevices.has(deviceId);
    });
  }

  filterPermissionTreeNodes(sitelist: any[]): any[] {

    if (!sitelist || sitelist.length === 0) {
      console.warn('[DEBUG] treeNodes is empty');
      return [];
    }

    const { moduleId } = this.getCurrentModule();
    const modules: any[] = this.getUserPermissions();
    const permissions: any[] = this.getModulePermissions(moduleId, modules);
    if (!permissions || permissions.length === 0) {
      console.warn('[DEBUG] no permissions matched for this module');
      return [];
    }

    const allowedDevices = new Set(permissions.map((p: any) => p.treeNode));

    const sensorPermissionMap = new Map(
      permissions.map((p: any) => [
        p.treeNode,
        new Set((p.sensor || []).map((s: any) => (typeof s === 'object' ? (s?.id ?? s?._id) : s)))
      ])
    );

    const getNodeId = (node: any): string | undefined => node?._id ?? node?.id;

    const filterLeafDevice = (node: any): any | null => {
      const nodeId = getNodeId(node);
      if (!node.sensors || node.sensors.length === 0) {
        return null;
      }
      if (!nodeId || !allowedDevices.has(nodeId)) {
        return null;
      }

      const allowedSensorIds = sensorPermissionMap.get(nodeId) || new Set();
        const filteredSensors = node.sensors.filter((sensor: any) => {
          const sensorId = sensor?.productSensor;
          return allowedSensorIds.has(sensorId);
        });

      if (filteredSensors.length === 0) {
        console.warn(`[DEBUG] Device "${node.textLabel || node.text}" matched but 0 sensors passed`);
        return null;
      }

      return { ...node, sensors: filteredSensors };
    };

    const flatResult: any[] = [];

    const collectLeaves = (nodes: any[]) => {
      for (const node of nodes) {
        const children = node.childItems || node.children || [];
        const isLeaf = children.length === 0;

        if (isLeaf) {
          const filtered = filterLeafDevice(node);
          if (filtered) {
            flatResult.push(filtered);
          }
        } else {
          collectLeaves(children);
        }
      }
    };

    collectLeaves(sitelist);
    return flatResult;
  }

  getModulePermissions(moduleId: string, modules: any[]): any[] {
    if (!moduleId || !modules) {
      return [];
    }
    for (const module of modules) {
      if (module?.id === moduleId || module?._id === moduleId) {
        return module?.devices || [];
      }
      const subModule = module?.subModules?.find(
        (sub: any) => sub?.id === moduleId || sub?._id === moduleId
      );
      if (subModule) {
        return subModule?.devices || [];
      }
    }
    return [];
  }

  getCurrentModule(): any {
    const moduleSlug = this.getCurrentModuleSlug();
    const findModule = this.getModuleBySlug(moduleSlug);
    if (findModule) {
      return {
        moduleId: findModule?.id ?? findModule?._id,
        selectedNode: findModule.defaultTreeNodeId,
      };
    }
    return { moduleId: '', selectedNode: '' };
  }

  getModuleBySlug(slug: string): any {
    const permissions = this.getUserPermissions?.() || [];
    const topLevelModule = permissions.find((m) => m.moduleSlug === slug);
    if (topLevelModule) {
      return topLevelModule;
    }

    for (const module of permissions) {
      const subModule = module?.subModules?.find((sub: any) => sub?.moduleSlug === slug);
      if (subModule) {
        return subModule;
      }
    }

    return null;
  }

  getCurrentModuleSlug(): string {
    let route = this.router.routerState.snapshot.root;
    let slug: string = route?.data?.['slug'] ?? '';

    while (route?.firstChild) {
      route = route.firstChild;
      if (route.data?.['slug']) {
        slug = route.data['slug'];
      }
    }

    return slug;
  }
}
